import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Separator } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldCombobox } from '~/components/FormField';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { useLabelList } from '~/modules/settings/api/queries/labelList';
import { Drawer } from '~/modules/settings/components/Drawer';
import {
  EXTERNAL_ROLES,
  LABEL_TYPES,
} from '~/modules/settings/constants/Filter';
import type { LabelsFilter } from '~/modules/settings/types/filter';

const defaultValues: LabelsFilter = {
  roles: [],
  labelType: 'all',
  parentIds: [],
};
const isFilterKey = (key: string): key is keyof LabelsFilter =>
  key in defaultValues;

interface UserDetailFormFilterProps {
  open: boolean;
  onApply: () => void;
}

export const UserDetailFormFilter = ({
  open,
  onApply,
}: UserDetailFormFilterProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { labelList } = useLabelList({
    labelType: 'parent_label',
    skip: 0,
    take: 50,
  });

  const filterForm = useForm<LabelsFilter>({
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      searchParams.forEach((value, key) => {
        if (isFilterKey(key)) {
          const parsedValue = Array.isArray(filterForm.getValues(key))
            ? value.split(',').map((val) => ({ value: val }))
            : value;
          filterForm.setValue(key, parsedValue);
        }
      });
    }
  }, [filterForm, open, searchParams]);

  const handleClear = () => {
    filterForm.reset(defaultValues);
  };

  const handleApply = (filterData: LabelsFilter) => {
    const params = new URLSearchParams();

    for (const [key, filter] of Object.entries(filterData)) {
      if (Array.isArray(filter) && filter.length) {
        params.set(key, filter.map((item) => item.value).join(','));
      } else if (filter && !Array.isArray(filter) && filter !== 'all') {
        params.set(key, filter);
      } else if (filter === 'all' && params.has(key)) {
        params.delete(key);
      }
    }

    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
    onApply();
  };

  const labelTypeOptions = useMemo(
    () =>
      LABEL_TYPES.map((labelType) => ({
        value: labelType.value,
        label: t(labelType.label),
      })),
    [t],
  );
  const parentLabelOptions = useMemo(
    () =>
      labelList
        ? labelList.data.map((labelGroup) => ({
            value: labelGroup.id,
            label: labelGroup.name,
          }))
        : [],
    [labelList],
  );
  const externalRoles = useMemo(
    () =>
      EXTERNAL_ROLES.map((role) => ({
        value: role.value,
        label: t(role.label),
      })),
    [t],
  );
  return (
    <FormProvider {...filterForm}>
      <Drawer.Header>
        <Drawer.Title>{t('filters')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <FormFieldRadioGroup
          name="labelType"
          label={t('labelType')}
          options={labelTypeOptions}
          className="gap-md"
        />
        <Separator className="my-md" />
        <FormFieldCombobox
          name="roles"
          label={t('role')}
          multiselect={true}
          displaySelectedOptions={true}
          options={externalRoles}
          placeholder={t('selectRole')}
        />
        <Separator className="my-md" />
        <FormFieldCombobox
          name="parentIds"
          label={t('parentLabel')}
          multiselect={true}
          displaySelectedOptions={true}
          options={parentLabelOptions}
          placeholder={t('label.placeholder.selectParentLabel')}
        />
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={handleClear}
          className="font-normal"
          type="button"
        >
          {t('Clear filters')}
        </Button>
        <Button
          size="large"
          type="button"
          onClick={filterForm.handleSubmit(handleApply)}
        >
          {t('Apply')}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
};

UserDetailFormFilter.displayName = 'UserDetailFormFilter';
